<template>
  <div class="entries-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-user-tie"></i>
        Fechamento por Vendedor
      </h1>
      <p class="page-subtitle">Consulte o balanço consolidado dos vendedores</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-filter"></i>
          Filtros de Consulta
        </h3>
      </div>
      
      <div class="filters-content">
        <div class="filters-grid">
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-users"></i>
              Vendedores *
            </label>
            <div class="multiselect-container">
              <MultiSelect 
                v-model="selectedSellers" 
                :options="availableSellers" 
                placeholder="Selecione os vendedores"
                :showSelectAllButtons="true"
              />
            </div>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Início *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="balanceDateBegin"
              placeholder="Data de início"
            />
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Fim *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="balanceDateEnd"
              placeholder="Data de fim"
            />
          </div>
        </div>

        <div class="filters-actions">
          <button 
            type="button" 
            class="btn-primary" 
            @click="searchBalance()"
            :disabled="loading"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-search"></i>
            {{ loading ? 'Consultando...' : 'Pesquisar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>
    </div>

    <!-- Tabela Principal: Fechamento por Vendedor -->
    <div v-show="!loading && balance && balance.sellers && balance.sellers.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-table"></i>
          Fechamento por Vendedor
        </h3>
        <div class="card-actions">
          <div class="table-zoom-controls" title="Zoom da tabela">
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom <= 5"
              @click="decreaseTableZoom"
              title="Diminuir zoom"
            >
              <i class="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
              {{ tableZoom }}%
            </span>
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom >= 100"
              @click="increaseTableZoom"
              title="Aumentar zoom"
            >
              <i class="fa-solid fa-magnifying-glass-plus"></i>
            </button>
          </div>
          <div class="columns-dropdown" ref="columnsDropdown">
            <button
              type="button"
              class="btn-action btn-columns"
              @click.stop="showColumnsMenu = !showColumnsMenu"
              title="Mostrar ou esconder colunas"
            >
              <i class="fa-solid fa-columns"></i>
              Colunas
              <i class="fa-solid fa-caret-down columns-caret"></i>
            </button>
            <div v-if="showColumnsMenu" class="columns-menu" @click.stop>
              <div class="columns-menu-header">
                <span>Colunas visíveis</span>
                <button type="button" class="columns-reset" @click="resetVisibleColumns">
                  Restaurar
                </button>
              </div>
              <label
                v-for="col in toggleableColumns"
                :key="col.key"
                class="columns-menu-item"
                :class="{ locked: col.locked }"
              >
                <input
                  type="checkbox"
                  :checked="isColumnVisible(col.key)"
                  :disabled="col.locked"
                  @change="toggleColumn(col.key)"
                />
                <span>{{ col.label }}</span>
              </label>
            </div>
          </div>
          <button
            type="button"
            class="btn-action btn-print"
            @click="printReport()"
            title="Imprimir relatório"
          >
            <i class="fa-solid fa-print"></i>
            Imprimir
          </button>
          <button
            type="button"
            class="btn-action btn-pdf"
            @click="exportToPDF()"
            title="Exportar para PDF"
          >
            <i class="fa-solid fa-file-pdf"></i>
            PDF
          </button>
          <button
            type="button"
            class="btn-action btn-excel"
            @click="exportToExcel()"
            title="Exportar para Excel"
          >
            <i class="fa-solid fa-file-excel"></i>
            Excel
          </button>
        </div>
      </div>
      
      <div class="table-container" :style="tableZoomStyle">
        <table class="modern-table">
          <thead>
            <tr>
              <th v-if="isColumnVisible('seller')">Vendedor</th>
              <th v-if="isColumnVisible('region')">Região</th>
              <th v-if="isColumnVisible('previousBalance')">Saldo Anterior</th>
              <th v-if="isColumnVisible('bet')">Vendas(+)</th>
              <th v-if="isColumnVisible('comission')">Comissão(-)</th>
              <th v-if="isColumnVisible('net')">Líquido</th>
              <th v-if="isColumnVisible('retainedPrizes')">Prêmios Retidos</th>
              <th v-if="isColumnVisible('totalAwarded')">Prêmios(-)</th>
              <th v-if="isColumnVisible('liquidoTwo')">{{ liquidoTwoColumnTitle }}</th>
              <th v-if="isColumnVisible('retorno')">Retorno</th>
              <th v-if="isColumnVisible('bancaRecebeu')">Banca Recebeu(+)</th>
              <th v-if="isColumnVisible('bancaPagou')">Banca Pagou(-)</th>
              <th v-if="isColumnVisible('currentBalance')">Saldo Atual</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="s in balance.sellers" 
              :key="s.sellerId"
              class="data-row"
            >
              <td v-if="isColumnVisible('seller')">
                <div class="seller-cell" @click="openBets(s.sellerId)">
                  <i class="fa-solid fa-user"></i>
                  <span class="seller-link">{{ s.sellerName }}</span>
                </div>
              </td>
              <td v-if="isColumnVisible('region')">
                <span class="region-name">{{ s.regionDescription }}</span>
              </td>
              <td v-if="isColumnVisible('previousBalance')">
                <span :class="['currency-value', { 'currency-positive': s.previousBalance > 0, 'currency-negative': s.previousBalance < 0 }]">{{ setCurrencyString(s.previousBalance) }}</span>
              </td>
              <td v-if="isColumnVisible('bet')">
                <span
                  v-if="(s.bet || 0) > 0"
                  class="currency-value currency-positive clickable-value"
                  @click="openSalesModal(s)"
                  title="Clique para ver as vendas"
                >
                  {{ setCurrencyString(s.bet) }}
                </span>
                <span v-else class="currency-value currency-positive">{{ setCurrencyString(s.bet) }}</span>
              </td>
              <td v-if="isColumnVisible('comission')">
                <span class="currency-value currency-negative">{{ setCurrencyString(s.comission) }}</span>
              </td>
              <td v-if="isColumnVisible('net')">
                <span :class="['currency-value', { 'currency-positive': s.net > 0, 'currency-negative': s.net < 0 }]">{{ setCurrencyString(s.net) }}</span>
              </td>
              <td v-if="isColumnVisible('retainedPrizes')">
                <span class="currency-value currency-negative">{{ setCurrencyString(s.retainedPrizes) }}</span>
              </td>
              <td v-if="isColumnVisible('totalAwarded')">
                <span class="currency-value currency-negative">{{ setCurrencyString(s.totalAwarded) }}</span>
              </td>
              <td v-if="isColumnVisible('liquidoTwo')">
                <span :class="['currency-value', { 'currency-positive': calculateLiquidoTwo(s) > 0, 'currency-negative': calculateLiquidoTwo(s) < 0 }]">{{ setCurrencyString(calculateLiquidoTwo(s)) }}</span>
              </td>
              <td v-if="isColumnVisible('retorno')">
                <span class="currency-value">{{ setCurrencyString(calculateReturnValue(s)) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaRecebeu')">
                <span class="currency-value currency-positive">{{ setCurrencyString(s.credit) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaPagou')">
                <span class="currency-value currency-negative">{{ setCurrencyString(s.debit) }}</span>
              </td>
              <td v-if="isColumnVisible('currentBalance')">
                <span :class="['currency-value', { 'currency-negative': s.currentBalance < 0, 'currency-positive': s.currentBalance > 0 }]">
                  {{ setCurrencyString(s.currentBalance) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td :colspan="totalLabelColspan">
                <div class="total-cell">
                  <i class="fa-solid fa-calculator"></i>
                  <span>Total</span>
                </div>
              </td>
              <td v-if="isColumnVisible('previousBalance')">
                <span :class="['currency-value', { 'currency-positive': balance?.previousBalanceAmount > 0, 'currency-negative': balance?.previousBalanceAmount < 0 }]">{{ setCurrencyString(balance?.previousBalanceAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('bet')">
                <span class="currency-value currency-positive">{{ setCurrencyString(balance?.betAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('comission')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.comissionAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('net')">
                <span :class="['currency-value', { 'currency-positive': balance?.netAmount > 0, 'currency-negative': balance?.netAmount < 0 }]">{{ setCurrencyString(balance?.netAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('retainedPrizes')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.retainedPrizes ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('totalAwarded')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.totalAwarded ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('liquidoTwo')">
                <span :class="['currency-value', { 'currency-positive': calculateTotalLiquidoTwo() > 0, 'currency-negative': calculateTotalLiquidoTwo() < 0 }]">{{ setCurrencyString(calculateTotalLiquidoTwo()) }}</span>
              </td>
              <td v-if="isColumnVisible('retorno')">
                <span class="currency-value">{{ setCurrencyString(calculateTotalReturnValue()) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaRecebeu')">
                <span class="currency-value currency-positive">{{ setCurrencyString(balance?.creditAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaPagou')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.debitAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('currentBalance')">
                <span :class="['currency-value', 'final-balance', { 'currency-negative': balance?.currentBalanceAmount < 0, 'currency-positive': balance?.currentBalanceAmount > 0 }]">
                  {{ setCurrencyString(balance?.currentBalanceAmount ?? 0) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>



    <!-- Estado vazio -->
    <div v-show="!loading && (!balance || !balance.sellers || balance.sellers.length === 0)" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-user-tie"></i>
        <h3>Nenhum dado encontrado</h3>
        <p>Selecione os vendedores e o período para consultar o fechamento</p>
      </div>
    </div>

    <!-- Modal de Vendas -->
    <PrizeTicketsModal
      v-if="showSalesModal"
      :sellerId="salesModalSellerId"
      :regionId="salesModalRegionId"
      :regionName="salesModalEntityName"
      :dateBegin="balanceDateBegin"
      :dateEnd="balanceDateEnd"
      mode="sales"
      @close="closeSalesModal"
    />
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import MultiSelect from "@/components/utils/MultiSelect.vue";
import PrizeTicketsModal from "@/components/PrizeTicketsModal.vue";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const ENTRIES_SELLER_COLUMNS_STORAGE_KEY = "entriesSeller.visibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_VISIBLE_COLUMNS = {
  seller: true,
  region: true,
  previousBalance: true,
  bet: true,
  comission: true,
  net: true,
  retainedPrizes: true,
  totalAwarded: true,
  liquidoTwo: true,
  retorno: true,
  bancaRecebeu: true,
  bancaPagou: true,
  currentBalance: true,
};

export default {
  components: { MultiSelect, PrizeTicketsModal },
  data: () => ({
    balance: {
      sellers: [],
      betAmount: 0
    },
    loading: false,
    sellers: [],
    selectedSellers: [],
    balanceDateBegin: "0000-00-00",
    balanceDateEnd: "0000-00-00",
    
    // Controle de usuário e permissões
    currentUser: null,
    userResponsibleRegions: [],
    userBancaId: null,
    bancas: [],
    regions: [],

    // Modal de vendas
    showSalesModal: false,
    salesModalSellerId: null,
    salesModalRegionId: null,
    salesModalEntityName: '',

    // Controle de colunas da tabela
    showColumnsMenu: false,
    visibleColumns: { ...DEFAULT_VISIBLE_COLUMNS },
    tableZoom: 100,
  }),
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },

    // Garante que selectedSellers seja sempre array (vue-multiselect pode emitir valor inválido ao digitar)
    selectedSellersSafe() {
      return Array.isArray(this.selectedSellers) ? this.selectedSellers : [];
    },

    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    availableSellers() {
      let sellers = this.sellers;
      
      if (this.isAdmin) {
        // ADMIN: aplicar filtro por banca selecionada primeiro se houver
        if (this.currentSelectedBanca) {
          // Filtrar vendedores pela banca selecionada através da região
          sellers = sellers.filter(seller => {
            return seller.bancaId && String(seller.bancaId) === String(this.currentSelectedBanca.id);
          });
        }
        
        // Para ADMIN, mostrar todos os vendedores (filtrados por banca se selecionada)
      } else if (this.isCobrador) {
        // COBRADOR: apenas vendedores vinculados (lista já vem de GetSellersByCollector)
        const collectorId = String(this.currentUser?.id || '');
        sellers = sellers.filter(seller => {
          if (!seller || !seller.id) return false;
          if (seller.collectorId != null || seller.CollectorId != null) {
            return String(seller.collectorId || seller.CollectorId) === collectorId;
          }
          return true;
        });

        // Se houver região ativa no cabeçalho, restringe também a ela
        if (this.currentSelectedRegion) {
          sellers = sellers.filter(seller =>
            String(seller.regionId) === String(this.currentSelectedRegion.id)
          );
        }
      } else {
        // BANCA e GERENTE: apenas vendedores das regiões que são responsáveis
        sellers = sellers.filter(seller => 
          this.userResponsibleRegions.includes(seller.regionId)
        );
        
        // Para usuários não-admin, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          sellers = sellers.filter(seller => {
            return String(seller.regionId) === String(this.currentSelectedRegion.id);
          });
        }
      }

      return [...sellers].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
      );
    },

    /** IDs dos vendedores vinculados ao cobrador (para validar consulta). */
    cobradorLinkedSellerIds() {
      if (!this.isCobrador) return null;
      const ids = new Set();
      (this.availableSellers || []).forEach(s => {
        if (s?.id != null) ids.add(String(s.id));
      });
      return ids;
    },

    shouldShowLiquido2AndRetorno() {
      // A coluna LP/Líquido 2 sempre aparece, apenas o título muda
      // Este método agora controla apenas se devemos mostrar como "Líquido 2" (true) ou "LP" (false)
      if (!this.selectedSellersSafe.length) {
        return false;
      }
      
      // Verificar se TODAS as regiões dos vendedores selecionados são de descarga
      const allSelectedSellersAreFromDescargaRegions = this.selectedSellersSafe.every(seller => {
        const fullSeller = this.sellers.find(s => s.id === seller.id);
        if (!fullSeller) return false;
        
        const region = this.regions.find(r => r.id === fullSeller.regionId);
        return region && region.onlyDescarga === true;
      });
      
      return allSelectedSellersAreFromDescargaRegions;
    },

    shouldShowRetorno() {
      // Mostrar Retorno apenas se há pelo menos uma região de descarga
      if (!this.selectedSellersSafe.length) {
        return false;
      }
      
      // Verificar se há pelo menos um vendedor de região de descarga
      return this.selectedSellersSafe.some(seller => {
        const fullSeller = this.sellers.find(s => s.id === seller.id);
        if (!fullSeller) return false;
        
        const region = this.regions.find(r => r.id === fullSeller.regionId);
        return region && region.onlyDescarga;
      });
    },

    shouldShowAcertoLP() {
      // Mostrar coluna Acerto L.P apenas se há pelo menos uma região de descarga
      if (!this.selectedSellersSafe.length) {
        return false;
      }
      
      // Verificar se há pelo menos um vendedor de região de descarga
      return this.selectedSellersSafe.some(seller => {
        const fullSeller = this.sellers.find(s => s.id === seller.id);
        if (!fullSeller) return false;
        
        const region = this.regions.find(r => r.id === fullSeller.regionId);
        return region && region.onlyDescarga;
      });
    },

    liquidoTwoColumnTitle() {
      // Se todas as regiões NÃO são de descarga, mostrar "LP", senão "Líquido 2"
      if (!this.selectedSellersSafe.length) {
        return 'Líquido 2'; // Padrão
      }
      
      // Verificar se TODAS as regiões dos vendedores selecionados NÃO são de descarga
      const allSelectedSellersAreNotFromDescargaRegions = this.selectedSellersSafe.every(seller => {
        const fullSeller = this.sellers.find(s => s.id === seller.id);
        if (!fullSeller) return false;
        
        const region = this.regions.find(r => r.id === fullSeller.regionId);
        return region && !region.onlyDescarga;
      });
      
      return allSelectedSellersAreNotFromDescargaRegions ? 'LP' : 'Líquido 2';
    },

    shouldShowRetainedPrizesColumn() {
      // Mostrar coluna "Prêmio Retido" apenas para regiões não de descarga (onlyDescarga = false)
      // Se TODAS as regiões têm onlyDescarga = true, então NÃO mostrar "Prêmio Retido"
      if (!this.selectedSellersSafe.length) {
        return true; // Mostrar por padrão se não há vendedores selecionados
      }
      
      // Verificar se TODAS as regiões dos vendedores selecionados são de descarga
      const allSelectedSellersAreFromDescargaRegions = this.selectedSellersSafe.every(seller => {
        const fullSeller = this.sellers.find(s => s.id === seller.id);
        if (!fullSeller) return false;
        
        const region = this.regions.find(r => r.id === fullSeller.regionId);
        return region && region.onlyDescarga === true;
      });
      
      // Se TODAS são de descarga, NÃO mostrar a coluna
      // Se pelo menos uma NÃO é de descarga, mostrar a coluna
      return !allSelectedSellersAreFromDescargaRegions;
    },

    toggleableColumns() {
      const columns = [
        { key: "seller", label: "Vendedor", locked: true },
        { key: "region", label: "Região" },
        { key: "previousBalance", label: "Saldo Anterior" },
        { key: "bet", label: "Vendas(+)" },
        { key: "comission", label: "Comissão(-)" },
        { key: "net", label: "Líquido" },
      ];

      if (this.shouldShowRetainedPrizesColumn) {
        columns.push({ key: "retainedPrizes", label: "Prêmios Retidos" });
      }

      columns.push(
        { key: "totalAwarded", label: "Prêmios(-)" },
        { key: "liquidoTwo", label: this.liquidoTwoColumnTitle },
      );

      if (this.shouldShowRetorno) {
        columns.push({ key: "retorno", label: "Retorno" });
      }

      columns.push(
        { key: "bancaRecebeu", label: "Banca Recebeu(+)" },
        { key: "bancaPagou", label: "Banca Pagou(-)" },
        { key: "currentBalance", label: "Saldo Atual" },
      );

      return columns;
    },

    totalLabelColspan() {
      let count = 0;
      if (this.isColumnVisible("seller")) count += 1;
      if (this.isColumnVisible("region")) count += 1;
      return Math.max(count, 1);
    },
  },
  async mounted() {
    this.loadVisibleColumns();
    this.loadTableZoom();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    await this.loadCurrentUser();
    
    const _d = new Date();
    const _dateStr = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
    this.balanceDateBegin = _dateStr;
    this.balanceDateEnd = _dateStr;
    
    await this.searchSellers();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  watch: {
    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Quando a região ativa mudar, recarregar os vendedores disponíveis
        this.updateSelectedSellers();
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Quando a banca ativa mudar, recarregar os vendedores disponíveis
        this.updateSelectedSellers();
      },
      deep: true
    },

    selectedSellers: {
      handler(newSellers) {
        // Ignorar se vue-multiselect emitir valor inválido (ex: ao digitar no search)
        if (!Array.isArray(newSellers)) return;
        this.$forceUpdate();
        // Filtrar automaticamente conforme o usuário adiciona/remove vendedores
        if (newSellers.length > 0 && this.balanceDateBegin && this.balanceDateEnd) {
          this.searchBalance();
        } else if (newSellers.length === 0) {
          this.balance = { sellers: [], betAmount: 0 };
        }
      },
      deep: true
    }
  },
  methods: {
    loadVisibleColumns() {
      try {
        const saved = localStorage.getItem(ENTRIES_SELLER_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          seller: true,
        };
      } catch {
        this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      }
    },

    saveVisibleColumns() {
      localStorage.setItem(
        ENTRIES_SELLER_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visibleColumns),
      );
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    isColumnAllowedByBusiness(key) {
      switch (key) {
        case "retainedPrizes":
          return this.shouldShowRetainedPrizesColumn;
        case "retorno":
          return this.shouldShowRetorno;
        default:
          return true;
      }
    },

    isColumnVisible(key) {
      if (!this.isColumnAllowedByBusiness(key)) {
        return false;
      }
      if (key === "seller") {
        return true;
      }
      return this.visibleColumns[key] !== false;
    },

    toggleColumn(key) {
      if (key === "seller") {
        return;
      }
      this.visibleColumns = {
        ...this.visibleColumns,
        [key]: !this.isColumnVisible(key),
      };
      this.saveVisibleColumns();
    },

    resetVisibleColumns() {
      this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      this.saveVisibleColumns();
    },

    closeColumnsMenu() {
      this.showColumnsMenu = false;
    },

    handleColumnsMenuOutsideClick(event) {
      if (!this.showColumnsMenu) {
        return;
      }
      const dropdown = this.$refs.columnsDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.closeColumnsMenu();
      }
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
        } else {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push('/login');
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        if (this.isBanca) {
          // Para BANCA: buscar o BancaId do usuário e depois filtrar regiões
          const bancaResponse = await mainService.getUserBancaId(this.currentUser.id);
          this.userBancaId = bancaResponse.data;

          if (this.userBancaId) {
            // Buscar todas as regiões e filtrar por BancaId
            const allRegionsResponse = await mainService.searchRegions();
            const allRegions = allRegionsResponse.data || [];
            
            // Filtrar regiões que pertencem à banca do usuário
            const bancaRegions = allRegions.filter(region => region.bancaId === this.userBancaId);
            this.userResponsibleRegions = bancaRegions.map(region => region.id);

          } else {
            this.userResponsibleRegions = [];
            toast.error("Usuário BANCA não possui BancaId definido!");
          }
        } else {
          // Para GERENTE e COBRADOR: usar a API de regiões responsáveis
          const response = await mainService.getUserRegions(this.currentUser.id);
          this.userResponsibleRegions = response.data.map(region => region.id);
          
          // Incluir a região principal também, se existir
          if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
            this.userResponsibleRegions.push(this.currentUser.regionId);
          }
          
        }
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário:', error);
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    searchBalance() {
      const sellers = this.selectedSellersSafe;
      if (sellers.length === 0) {
        toast.warning("Selecione um ou mais Vendedores!");
        return;
      }

      // COBRADOR: só consulta vendedores vinculados
      if (this.isCobrador) {
        const allowed = this.cobradorLinkedSellerIds;
        if (!allowed || allowed.size === 0) {
          toast.info('Nenhum vendedor vinculado ao seu usuário de cobrador');
          return;
        }
        const invalid = sellers.filter(s => !allowed.has(String(s.id)));
        if (invalid.length > 0) {
          this.selectedSellers = sellers.filter(s => allowed.has(String(s.id)));
          toast.warning('Removidos vendedores não vinculados ao cobrador');
          if (this.selectedSellersSafe.length === 0) {
            return;
          }
        }
      }

      if (this.balanceDateBegin == null || this.balanceDateBegin == "") {
        toast.warning("Selecione a data Inicial!");
        return;
      }
      if (this.balanceDateEnd == null || this.balanceDateEnd == "") {
        toast.warning("Selecione a data Final!");
        return;
      }
      
      this.loading = true;

      const dateBeginStr = this.balanceDateBegin + "T00:00:00";
      const dateEndStr = this.balanceDateEnd + "T23:59:59";

      if (new Date(dateBeginStr) > new Date(dateEndStr)) {
        toast.error("A data inicial não pode ser maior que a data final!");
        this.loading = false;
        return;
      }
      
      let request = {
        sellerIds: this.selectedSellersSafe.map(c => c.id),
        balanceDateBegin: dateBeginStr,
        balanceDateEnd: dateEndStr,
      };

      mainService
        .searchSellerBalance(request)
        .then((response) => {
          const data = response.data || {};
          let sellersResult = [...(data.sellers || [])];

          // Segurança extra: cobrador só vê linhas dos vinculados
          if (this.isCobrador && this.cobradorLinkedSellerIds) {
            const allowed = this.cobradorLinkedSellerIds;
            sellersResult = sellersResult.filter(s =>
              allowed.has(String(s.sellerId || s.SellerId || s.id))
            );
          }

          this.balance = {
            ...data,
            sellers: sellersResult.sort((a, b) =>
              (a.sellerName || '').localeCompare(b.sellerName || '', 'pt-BR', { sensitivity: 'base' })
            )
          };
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    async searchSellers() {
      try {
        // Carregar bancas e regiões primeiro
        if (!this.bancas || this.bancas.length === 0) {
          await this.loadBancas();
        }
        if (!this.regions || this.regions.length === 0) {
          await this.loadRegions();
        }
        
        // Aguardar carregamento das regiões do usuário se não for ADMIN
        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
        }

        if (this.isAdmin) {
          // ADMIN busca todos os vendedores
          try {
            const response = await mainService.searchSellers();
            const allSellers = response.data || [];
            
            // Adicionar informações de banca a cada vendedor
            this.sellers = allSellers.map(seller => {
              // Buscar a região do vendedor para obter a banca
              const sellerRegion = this.regions.find(r => r.id === seller.regionId);
              const sellerBanca = sellerRegion ? this.bancas.find(b => b.id === sellerRegion.bancaId) : null;
              
              return {
                ...seller,
                bancaId: sellerBanca?.id || null,
                bancaName: sellerBanca?.name || '-',
              };
            });
          } catch (error) {
            console.error('Erro ao carregar vendedores:', error);
            this.sellers = [];
          }
        } else if (this.isCobrador && this.currentUser?.id) {
          // COBRADOR: apenas vendedores vinculados a ele
          try {
            const response = await mainService.getSellersByCollector(this.currentUser.id);
            const list = Array.isArray(response.data) ? response.data : [];
            this.sellers = list.map(seller => {
              const sellerRegion = this.regions.find(r => r.id === seller.regionId);
              const sellerBanca = sellerRegion
                ? this.bancas.find(b => b.id === sellerRegion.bancaId)
                : null;

              return {
                ...seller,
                collectorId: seller.collectorId || seller.CollectorId || this.currentUser.id,
                bancaId: sellerBanca?.id || null,
                bancaName: sellerBanca?.name || '-',
              };
            });
          } catch (error) {
            console.error('Erro ao carregar vendedores do cobrador:', error);
            this.sellers = [];
            toast.error('Erro ao carregar vendedores vinculados ao cobrador');
          }
        } else {
          // BANCA e GERENTE buscam vendedores de suas regiões
          if (this.userResponsibleRegions.length > 0) {
            try {
              const promises = this.userResponsibleRegions.map(regionId => 
                mainService.searchSellersByRegion(regionId)
              );
              
              const responses = await Promise.all(promises);
              
              // Combinar resultados de todas as regiões
              let allSellers = [];
              responses.forEach(response => {
                if (response.data && Array.isArray(response.data)) {
                  allSellers = allSellers.concat(response.data);
                }
              });
              
              // Remover duplicatas baseado no ID e adicionar informações de banca
              const uniqueSellers = allSellers.filter((seller, index, self) => 
                index === self.findIndex(s => s.id === seller.id)
              ).map(seller => {
                // Buscar a região do vendedor para obter a banca
                const sellerRegion = this.regions.find(r => r.id === seller.regionId);
                const sellerBanca = sellerRegion ? this.bancas.find(b => b.id === sellerRegion.bancaId) : null;
                
                return {
                  ...seller,
                  bancaId: sellerBanca?.id || null,
                  bancaName: sellerBanca?.name || '-',
                };
              });
              
              this.sellers = uniqueSellers;
            } catch (error) {
              console.error('Erro ao carregar vendedores:', error);
              this.sellers = [];
            }
          } else {
            this.sellers = [];
            toast.warning("Nenhuma região definida para o usuário!");
          }
        }
        
        // Garantir que ao entrar na página nenhum vendedor venha pré-selecionado
        this.selectedSellers = [];
      } catch (error) {
        console.error('Erro ao buscar vendedores:', error);
        toast.error(handleErrorMessage(error));
        this.sellers = [];
      }
    },

    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar bancas:', error);
      }
    },

    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar regiões:', error);
        this.regions = [];
      }
    },

    updateSelectedSellers() {
      // Ao trocar de região/banca, limpar seleção e resultados para o usuário selecionar manualmente
      this.selectedSellers = [];
      this.balance = { sellers: [], betAmount: 0 };
    },
    
    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },
    
    openBets(sellerId) {
      // Verificar se o usuário tem permissão para acessar este vendedor
      const seller = this.sellers.find(s => s.id === sellerId);
      if (!this.isAdmin && !this.userResponsibleRegions.some(id => String(id) === String(seller?.regionId))) {
        toast.error("Você não tem permissão para acessar dados deste vendedor!");
        return;
      }
      
      try {
        this.$router.push({
          path: "/entriesnumbers",
          query: {
            sellerId: String(sellerId),
            ...(seller?.regionId ? { regionId: String(seller.regionId) } : {}),
            StartDate: `${this.balanceDateBegin}T00:00:00`,
            EndDate: `${this.balanceDateEnd}T23:59:59`,
          },
        });
      } catch (error) {
        console.error('Erro ao abrir página de apostas:', error);
        toast.error("Erro ao abrir página de detalhes das apostas");
      }
    },

    openSalesModal(seller) {
      if (!seller || (seller.bet || 0) <= 0) return;
      const fullSeller = this.sellers.find(s => String(s.id) === String(seller.sellerId));
      this.salesModalSellerId = seller.sellerId;
      this.salesModalRegionId = fullSeller?.regionId || null;
      this.salesModalEntityName = seller.sellerName || seller.regionDescription || '';
      this.showSalesModal = true;
    },

    closeSalesModal() {
      this.showSalesModal = false;
      this.salesModalSellerId = null;
      this.salesModalRegionId = null;
      this.salesModalEntityName = '';
    },

    calculateReturnValue(seller) {
      // Calcular o Líquido 2 primeiro
      const liquidoTwo = this.calculateLiquidoTwo(seller);
      
      // Só calcular retorno se o Líquido 2 for positivo
      if (liquidoTwo > 0) {
        // Retorno = (Líquido 2 * DescargaReturnPercent) / 100
        // Se DescargaReturnPercent não existir, usar 0
        const returnPercent = seller.descargaReturnPercent || 0;
        return (liquidoTwo * returnPercent) / 100;
      }
      return 0;
    },
 
    calculateTotalReturnValue() {
      let totalReturnValue = 0;
      this.balance.sellers.forEach(seller => {
        totalReturnValue += this.calculateReturnValue(seller);
      });
      return totalReturnValue;
    },

    calculateLiquidoTwo(seller) {
      // Líquido 2 = (Líquido - Prêmios Pagos)
      // Nota: Agora só consideramos prêmios efetivamente pagos, não os retidos
      const net = seller.net || 0;
      const totalAwarded = seller.totalAwarded || 0;
      return net - totalAwarded;
    },

    calculateTotalLiquidoTwo() {
      let totalLiquidoTwo = 0;
      this.balance.sellers.forEach(seller => {
        totalLiquidoTwo += this.calculateLiquidoTwo(seller);
      });
      return totalLiquidoTwo;
    },

    selectedSellersLabel() {
      return this.selectedSellersSafe
        .map(s => s.name || s.sellerName || s.label || s.displayName)
        .filter(Boolean)
        .join(", ");
    },

    /** Cabeçalhos das colunas visíveis (mesma ordem da tabela). */
    getExportHeaders() {
      const headers = [];
      if (this.isColumnVisible("seller")) headers.push("Vendedor");
      if (this.isColumnVisible("region")) headers.push("Região");
      if (this.isColumnVisible("previousBalance")) headers.push("Saldo Anterior");
      if (this.isColumnVisible("bet")) headers.push("Vendas(+)");
      if (this.isColumnVisible("comission")) headers.push("Comissão(-)");
      if (this.isColumnVisible("net")) headers.push("Líquido");
      if (this.isColumnVisible("retainedPrizes")) headers.push("Prêmios Retidos");
      if (this.isColumnVisible("totalAwarded")) headers.push("Prêmios(-)");
      if (this.isColumnVisible("liquidoTwo")) headers.push(this.liquidoTwoColumnTitle);
      if (this.isColumnVisible("retorno")) headers.push("Retorno");
      if (this.isColumnVisible("bancaRecebeu")) headers.push("Banca Recebeu(+)");
      if (this.isColumnVisible("bancaPagou")) headers.push("Banca Pagou(-)");
      if (this.isColumnVisible("currentBalance")) headers.push("Saldo Atual");
      return headers;
    },

    /** Linha do vendedor; formatCurrency=true formata valores em BRL (PDF/impressão). */
    getExportRow(seller, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v || 0) : v || 0;
      const row = [];
      if (this.isColumnVisible("seller")) row.push(seller.sellerName || "");
      if (this.isColumnVisible("region")) row.push(seller.regionDescription || "");
      if (this.isColumnVisible("previousBalance")) row.push(money(seller.previousBalance));
      if (this.isColumnVisible("bet")) row.push(money(seller.bet));
      if (this.isColumnVisible("comission")) row.push(money(seller.comission));
      if (this.isColumnVisible("net")) row.push(money(seller.net));
      if (this.isColumnVisible("retainedPrizes")) row.push(money(seller.retainedPrizes));
      if (this.isColumnVisible("totalAwarded")) row.push(money(seller.totalAwarded));
      if (this.isColumnVisible("liquidoTwo")) row.push(money(this.calculateLiquidoTwo(seller)));
      if (this.isColumnVisible("retorno")) row.push(money(this.calculateReturnValue(seller)));
      if (this.isColumnVisible("bancaRecebeu")) row.push(money(seller.credit));
      if (this.isColumnVisible("bancaPagou")) row.push(money(seller.debit));
      if (this.isColumnVisible("currentBalance")) row.push(money(seller.currentBalance));
      return row;
    },

    /** Linha de totais (igual ao rodapé da tabela na página). */
    getTotalExportRow(formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const b = this.balance || {};
      const row = [];
      let labelPlaced = false;
      if (this.isColumnVisible("seller")) {
        row.push("Total");
        labelPlaced = true;
      }
      if (this.isColumnVisible("region")) {
        row.push(labelPlaced ? "" : "Total");
      }
      if (this.isColumnVisible("previousBalance")) row.push(money(b.previousBalanceAmount));
      if (this.isColumnVisible("bet")) row.push(money(b.betAmount));
      if (this.isColumnVisible("comission")) row.push(money(b.comissionAmount));
      if (this.isColumnVisible("net")) row.push(money(b.netAmount));
      if (this.isColumnVisible("retainedPrizes")) row.push(money(b.retainedPrizes));
      if (this.isColumnVisible("totalAwarded")) row.push(money(b.totalAwarded));
      if (this.isColumnVisible("liquidoTwo")) row.push(money(this.calculateTotalLiquidoTwo()));
      if (this.isColumnVisible("retorno")) row.push(money(this.calculateTotalReturnValue()));
      if (this.isColumnVisible("bancaRecebeu")) row.push(money(b.creditAmount));
      if (this.isColumnVisible("bancaPagou")) row.push(money(b.debitAmount));
      if (this.isColumnVisible("currentBalance")) row.push(money(b.currentBalanceAmount));
      return row;
    },

    printReport() {
      if (!this.balance || !this.balance.sellers || this.balance.sellers.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Não foi possível abrir a janela de impressão");
        return;
      }
      const printContent = this.generatePrintHTML();

      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generatePrintHTML() {
      const sellers = this.balance?.sellers || [];
      const headers = this.getExportHeaders();
      const headerRow = headers.map(h => `<th>${h}</th>`).join("");

      let tableRows = "";
      sellers.forEach(seller => {
        const cells = this.getExportRow(seller, true)
          .map((cell, idx) => {
            const isName = headers[idx] === "Vendedor" || headers[idx] === "Região";
            const align = isName ? "left" : "right";
            const bold = headers[idx] === "Saldo Atual" ? "font-weight: bold;" : "";
            return `<td style="text-align: ${align}; ${bold}">${cell}</td>`;
          })
          .join("");
        tableRows += `<tr>${cells}</tr>`;
      });

      const totalCells = this.getTotalExportRow(true)
        .map((cell, idx) => {
          const isName = headers[idx] === "Vendedor" || headers[idx] === "Região";
          const align = isName || idx === 0 ? "left" : "right";
          return `<td style="text-align: ${align}; font-weight: bold;">${cell}</td>`;
        })
        .join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Fechamento por Vendedor</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              body { margin: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .total-row td {
              background-color: #e8eef7;
              font-weight: bold;
              border-top: 2px solid #333;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Fechamento por Vendedor</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.balanceDateBegin} até ${this.balanceDateEnd}</p>
            <p><strong>Vendedores:</strong> ${this.selectedSellersLabel()}</p>
            <p><strong>Data de geração:</strong> ${jsFunctions.setDateTimeDisplay(new Date().toISOString())}</p>
          </div>
          <table>
            <thead>
              <tr>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
            <tfoot>
              <tr class="total-row">${totalCells}</tr>
            </tfoot>
          </table>
          <div class="footer">
            <p>Total de registros: ${sellers.length}</p>
          </div>
        </body>
        </html>
      `;
    },

    exportToPDF() {
      if (!this.balance || !this.balance.sellers || this.balance.sellers.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");

        doc.setFontSize(16);
        doc.text("Relatório de Fechamento por Vendedor", 14, 15);

        doc.setFontSize(10);
        doc.text(`Período: ${this.balanceDateBegin} até ${this.balanceDateEnd}`, 14, 25);
        const sellersText = doc.splitTextToSize(
          `Vendedores: ${this.selectedSellersLabel()}`,
          260,
        );
        doc.text(sellersText, 14, 30);
        const afterSellersY = 30 + Math.max(0, sellersText.length - 1) * 4;
        doc.text(
          `Data de geração: ${jsFunctions.setDateTimeDisplay(new Date().toISOString())}`,
          14,
          afterSellersY + 5,
        );

        const tableColumns = this.getExportHeaders();
        const tableData = (this.balance?.sellers || []).map(seller =>
          this.getExportRow(seller, true),
        );

        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          foot: [this.getTotalExportRow(true)],
          startY: afterSellersY + 10,
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          footStyles: {
            fillColor: [232, 238, 247],
            textColor: [0, 0, 0],
            fontStyle: "bold",
          },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap",
          showFoot: "lastPage",
        });

        const fileName = `Fechamento_Vendedor_${this.balanceDateBegin}_${this.balanceDateEnd}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      if (!this.balance || !this.balance.sellers || this.balance.sellers.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const headers = this.getExportHeaders();
        const tableData = (this.balance?.sellers || []).map(seller =>
          this.getExportRow(seller, false),
        );

        const wb = XLSX.utils.book_new();

        const allData = [
          ["Relatório de Fechamento por Vendedor"],
          [""],
          ["Período:", `${this.balanceDateBegin} até ${this.balanceDateEnd}`],
          ["Vendedores:", this.selectedSellersLabel()],
          ["Data de geração:", jsFunctions.setDateTimeDisplay(new Date().toISOString())],
          [""],
          headers,
          ...tableData,
        ];

        const ws = XLSX.utils.aoa_to_sheet(allData);
        ws["!cols"] = headers.map(() => ({ wch: 15 }));
        XLSX.utils.book_append_sheet(wb, ws, "Fechamento Vendedor");

        const fileName = `Fechamento_Vendedor_${this.balanceDateBegin}_${this.balanceDateEnd}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar Excel:", error);
        toast.error("Erro ao exportar Excel");
      }
    },
  },
};
</script>

<style scoped>
/* IMPORTANTE: Garantir que os dropdowns dos MultiSelect sobreponham todos os elementos */
/* Remover overflow: hidden dos containers para permitir que o dropdown apareça */
.entries-container .filters-card {
  overflow: visible !important;
}

.filters-content {
  overflow: visible !important;
}

.filters-grid {
  overflow: visible !important;
}

.input-group {
  overflow: visible !important;
  position: relative !important;
  z-index: 1 !important;
}

.multiselect-container {
  position: relative !important;
  z-index: 10 !important;
}

/* Quando o multiselect estiver ativo/aberto, aumentar o z-index */
.multiselect-container:has(.multiselect--active) {
  z-index: 1000 !important;
}

/* Table container com scroll horizontal */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

/* Tabela moderna com texto sem quebra de linha */
.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: nowrap;
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Células específicas */
.seller-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
  white-space: nowrap;
}

.seller-link {
  color: var(--accent-color);
  cursor: pointer;
  text-decoration: underline;
}

.seller-link:hover {
  color: var(--accent-hover);
}

.region-name {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-style: italic;
}

/* Valores monetários */
.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.clickable-value {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: all 0.2s ease;
}

.clickable-value:hover {
  opacity: 0.8;
  transform: scale(1.05);
  text-decoration-style: solid;
}

.clickable-value:active {
  transform: scale(0.98);
}

.currency-positive {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #10b981 !important;
}

.currency-negative {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #ef4444 !important;
}

.balance-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 600;
  color: var(--text-primary);
}

.balance-value.final-balance {
  font-size: 1.05rem;
  background: rgba(96, 165, 250, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

/* Total row */
.total-row {
  background: rgba(96, 165, 250, 0.1);
  font-weight: 600;
}

.total-row td {
  border-top: 2px solid var(--accent-color);
  font-weight: 600;
}

.total-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  color: var(--accent-color);
  font-weight: 600;
}

/* Responsividade para telas pequenas */
@media (max-width: 768px) {
  .modern-table {
    font-size: 0.8rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 480px) {
  .modern-table th,
  .modern-table td {
    padding: 0.375rem 0.5rem;
  }
}

/* Botões de ação — sobrescreve .btn-action global (32x32 ícone) */
.results-card {
  overflow: visible !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

.btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn-action:active {
  transform: translateY(0);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .card-actions {
    width: 100%;
  }

  .btn-action {
    flex: 1 1 auto;
    justify-content: center;
    width: auto !important;
    height: auto !important;
  }

  .columns-dropdown {
    flex: 1 1 auto;
  }

  .columns-dropdown .btn-action {
    width: 100% !important;
  }

  .columns-menu {
    left: 0;
    right: 0;
    min-width: 0;
  }
}
</style> 